import { zodResolver } from '@hookform/resolvers/zod';
import { type Resolver } from 'react-hook-form';
import { z } from 'zod';
import {
  DEFAULT_LIVE_FEED_ROTATION_SECONDS,
  MAX_LIVE_FEED_ROTATION_SECONDS,
  MIN_LIVE_FEED_ROTATION_SECONDS,
} from '../../constants';

const REQUIRED = 'required';
const MIN_VALUE_ZERO_MINUTE = 'minValueOneMinute';
const MAX_SECONDS = 'maxSeconds';
const MIN_ROTATION_SECONDS = 'minRotationSeconds';
const MAX_ROTATION_SECONDS = 'maxRotationSeconds';

// FIXME: i18n
const TRANSLATIONS: Record<string, string> = {
  [REQUIRED]: 'This field is required.',
  [MIN_VALUE_ZERO_MINUTE]: 'Value cannot be negative.',
  [MAX_SECONDS]: 'Value must be less than 60 seconds.',
  [MIN_ROTATION_SECONDS]: `Value must be at least ${MIN_LIVE_FEED_ROTATION_SECONDS} seconds.`,
  [MAX_ROTATION_SECONDS]: `Value must be at most ${MAX_LIVE_FEED_ROTATION_SECONDS} seconds.`,
};

// This is the schema for the widget configuration form.  We include all possible
// values for all widget types, so that if you switch types and then switch back,
// your previous values are still there.  This is also a very "flat" schema, because
// that's easier to work with in react-hook-form.
//
// Note that this is also used for validation.  Validation messages should come
// from the translations object above, so we can translate them.
//
const BaseWidgetSchema = z.object({
  title: z.string().min(1, REQUIRED),
  tagIds: z.array(z.string()),
});

/**
 * The window-based horizons, i.e. every horizon except `realtime`. Used by the widget types
 * that have no meaningful live value: DRIVE_OFF is inherently historical, and a journey is
 * only known once the vehicle has left the end zone.
 */
const WindowHorizonEnum = z.enum(['15m', '1h', 'currentHour', 'currentDay', 'currentDayPart']);

const HorizonEnum = z.enum([
  'realtime',
  '15m',
  '1h',
  'currentHour',
  'currentDay',
  'currentDayPart',
]);

export const WIDGET_FORM_SCHEMA = z.discriminatedUnion('type', [
  BaseWidgetSchema.extend({
    type: z.literal('COUNT'),
    countThreshold: z.number().min(0).default(0),
    horizon: HorizonEnum,
  }),
  BaseWidgetSchema.extend({
    type: z.literal('TIME'),
    timeThresholdMinutes: z.number().min(0, MIN_VALUE_ZERO_MINUTE).default(0),
    timeThresholdSeconds: z.number().min(0).max(59, MAX_SECONDS).default(0),
    horizon: HorizonEnum,
  }),
  BaseWidgetSchema.extend({
    type: z.literal('DRIVE_OFF'),
    countThreshold: z.number().min(0).default(0),
    // DRIVE_OFF supports the window-based horizons (no 'realtime' — drive-off is inherently
    // historical).
    horizon: WindowHorizonEnum.default('1h'),
  }),
  BaseWidgetSchema.extend({
    type: z.literal('TOTAL_JOURNEY_TIME'),
    timeThresholdMinutes: z.number().min(0, MIN_VALUE_ZERO_MINUTE).default(0),
    timeThresholdSeconds: z.number().min(0).max(59, MAX_SECONDS).default(0),
    horizon: WindowHorizonEnum,
  }),
  // LIVE_FEED shows cameras rather than aggregating events, so it has neither a threshold nor
  // a horizon — its tags decide which cameras to stream, and the rotation interval how long
  // each one stays on screen.
  BaseWidgetSchema.extend({
    type: z.literal('LIVE_FEED'),
    rotationSeconds: z
      .number()
      .min(MIN_LIVE_FEED_ROTATION_SECONDS, MIN_ROTATION_SECONDS)
      .max(MAX_LIVE_FEED_ROTATION_SECONDS, MAX_ROTATION_SECONDS)
      .default(DEFAULT_LIVE_FEED_ROTATION_SECONDS),
  }),
]);

export type WidgetFormData = z.infer<typeof WIDGET_FORM_SCHEMA>;

/** Create a resolver that translates errors messages from the schema. */
export function i18nZodResolver(): Resolver<WidgetFormData> {
  const resolver = zodResolver(WIDGET_FORM_SCHEMA);
  return async (values, context, options) => {
    const result = await resolver(values, context, options as any);
    for (const err of Object.values(result?.errors || {})) {
      if (err && typeof err === 'object' && 'message' in err) {
        const translation = TRANSLATIONS[err.message || ''];
        err.message = translation || err.message;
      }
    }
    return result;
  };
}
